/*
 * The Edit Dubai: product card built in JavaScript.
 *
 * Same card as snippets/product-item.liquid (badges: snippets/te-card-badges.liquid, heart and add button:
 * snippets/te-card-actions.liquid), for grids rendered in the browser: the Boost collection and search pages
 * (assets/bc-sf-filter.js). Keep the markup of the two in sync.
 *
 * Settings come from window.teProductCardConfig (snippets/te-product-card-config.liquid). The heart's saved state and
 * all clicks are handled by assets/te-sections.js, which also picks up cards added later.
 *
 * window.teProductCard.render(product) takes:
 *   handle, id, title, vendor, url, available, templateSuffix, tags [], collections [] (handles),
 *   variants [{ available, inventoryQuantity, inventoryManagement, inventoryPolicy }], variantId, variantCount,
 *   priceMin (cents), priceVaries, onSale, priceText, compareAtText (money already formatted),
 *   images [{ src (with {width}), widths, width, height, alt }] (featured image first, second image next),
 *   publishedAt (date text), cardBadge (the Card badge field's text), hideBadges (the Hide badges field),
 *   showLabels (default true), hideActions (default false)
 */
(function () {
  'use strict';

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  function isBlank(value) {
    return value == null || String(value).trim() === '';
  }

  // A title typed in ALL CAPITALS is shown in title case ("NICOLA BELT" -> "Nicola Belt"); others are left as typed
  function cardTitle(title) {
    title = String(title == null ? '' : title);

    if (title !== title.toUpperCase()) {
      return title;
    }

    return title.toLowerCase().split(/\s+/).filter(Boolean).map(function (word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
  }

  function badgeHtml(text, bg, fg) {
    return '<span class="te-card__badge" style="--te-badge-bg: ' + escapeHtml(bg) + '; --te-badge-fg: ' + escapeHtml(fg) + ';">'
      + escapeHtml(text) + '</span>';
  }

  // Stock left when every available variant is tracked by Shopify and can't be sold past zero, otherwise null
  function stockLeft(variants) {
    var left = 0;

    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];

      if (!variant.available) {
        continue;
      }

      if (variant.inventoryManagement !== 'shopify' || variant.inventoryPolicy !== 'deny') {
        return null;
      }

      left += parseInt(variant.inventoryQuantity, 10) || 0;
    }

    return left;
  }

  // Colours for a Card badge field value: the matching choice in the settings, or the "Other badges" colours
  function choiceColours(value, config) {
    var key = String(value).trim().toLowerCase();
    var choices = config.choices || [];

    for (var i = 0; i < choices.length; i++) {
      if (!isBlank(choices[i].name) && choices[i].name === key) {
        return choices[i];
      }
    }

    return config.custom;
  }

  // Published within the last "Show New for" days
  function isNew(product, config) {
    var days = parseInt(config.newBadge && config.newBadge.days, 10) || 0;
    var published = Date.parse(product.publishedAt || '');

    return days > 0 && !isNaN(published) && Date.now() <= published + days * 86400000;
  }

  // Same order as snippets/te-card-badges.liquid: sold out, sale, Card badge field, "__label:" tag, rules 1-6, new,
  // low stock. A product with "Hide badges" ticked shows none.
  function badgesHtml(product, config) {
    if (product.hideBadges) {
      return '';
    }

    var limit = parseInt(config.badgeLimit, 10) || 1;
    var badges = [];

    function add(text, bg, fg) {
      if (badges.length < limit && !isBlank(text)) {
        badges.push(badgeHtml(String(text).trim(), bg, fg));
      }
    }

    if (!product.available && config.soldOut.show) {
      add(config.soldOut.text, config.soldOut.bg, config.soldOut.fg);
    }

    if (product.available && product.onSale && config.sale.show) {
      add(config.sale.text, config.sale.bg, config.sale.fg);
    }

    if (badges.length < limit && !isBlank(product.cardBadge)) {
      var colours = choiceColours(product.cardBadge, config);
      add(product.cardBadge, colours.bg, colours.fg);
    }

    if (badges.length < limit) {
      var tagList = product.tags || [];

      for (var i = 0; i < tagList.length; i++) {
        if (String(tagList[i]).indexOf('__label:') !== -1) {
          add(String(tagList[i]).split('__label:').pop(), config.custom.bg, config.custom.fg);
          break;
        }
      }
    }

    var collections = (product.collections || []).map(function (handle) { return String(handle).toLowerCase(); });
    var tags = (product.tags || []).map(function (tag) { return String(tag).toLowerCase(); });

    (config.rules || []).forEach(function (rule) {
      if (badges.length >= limit || isBlank(rule.label) || isBlank(rule.value)) {
        return;
      }

      var list = rule.match === 'collection' ? collections : tags;

      if (list.indexOf(rule.value) !== -1) {
        add(rule.label, rule.bg, rule.fg);
      }
    });

    if (badges.length < limit && config.newBadge && isNew(product, config)) {
      add(config.newBadge.text, config.newBadge.bg, config.newBadge.fg);
    }

    // Last: most products have only a few pieces, so it would otherwise hide the other badges
    var threshold = parseInt(config.lowStock.threshold, 10) || 0;

    if (badges.length < limit && product.available && threshold > 0 && !isBlank(config.lowStock.text)) {
      var left = stockLeft(product.variants || []);

      if (left !== null && left > 0 && left <= threshold) {
        add(String(config.lowStock.text).replace(/\[count\]/g, left), config.lowStock.bg, config.lowStock.fg);
      }
    }

    return badges.length ? '<div class="te-card__badges">' + badges.join('') + '</div>' : '';
  }

  function sizeAttributes(image) {
    return image.width && image.height ? ' width="' + escapeHtml(image.width) + '" height="' + escapeHtml(image.height) + '"' : '';
  }

  function imageHtml(image, className) {
    return '<img class="te-card__image' + className + ' Image--lazyLoad Image--fadeIn" data-src="' + escapeHtml(image.src) + '"'
      + ' data-widths="[' + escapeHtml(image.widths) + ']" data-sizes="auto"' + sizeAttributes(image) + ' alt="' + escapeHtml(image.alt) + '">';
  }

  function imagesHtml(product, config, url) {
    var images = product.images || [];
    var mainImage = images[0];
    var altImage = config.showSecondaryImage ? images[1] : null;
    var html = '<a href="' + url + '" class="te-card__image-link' + (altImage ? ' te-card__image-link--alt' : '') + '" tabindex="-1" aria-hidden="true">'
      + '<div class="te-card__frame">';

    // The second image comes first: the loading spinner (.Image__Loader) follows the image shown by default
    if (altImage) {
      html += imageHtml({ src: altImage.src, widths: altImage.widths, width: altImage.width, height: altImage.height, alt: '' }, ' te-card__image--alt');
    }

    if (mainImage) {
      html += imageHtml({ src: mainImage.src, widths: mainImage.widths, width: mainImage.width, height: mainImage.height, alt: mainImage.alt || product.title }, '')
        + '<span class="Image__Loader"></span>'
        + '<noscript><img class="te-card__image" src="' + escapeHtml(String(mainImage.src).replace('{width}', '600')) + '"' + sizeAttributes(mainImage)
        + ' alt="' + escapeHtml(mainImage.alt || product.title) + '"></noscript>';
    } else {
      html += '<span class="te-card__image te-card__placeholder"></span>';
    }

    return html + '</div></a>';
  }

  // Same as snippets/te-card-actions.liquid (and the heart of snippets/te-wishlist-button.liquid)
  function actionsHtml(product, config, url) {
    var labels = config.labels;
    var html = '';

    if (config.showWishlist !== false) {
      html += '<button type="button" class="te-wishlist-button te-wishlist-button--card" data-te-wishlist-toggle'
        + ' data-product-handle="' + escapeHtml(product.handle) + '"'
        + ' data-product-id="' + escapeHtml(product.id) + '"'
        + ' data-product-title="' + escapeHtml(product.title) + '"'
        + ' data-product-price="' + ((parseFloat(product.priceMin) || 0) / 100) + '"'
        + ' aria-pressed="false" aria-label="' + escapeHtml(labels.addToWishlist) + '">'
        + config.heartIcon
        + '</button>';
    }

    if (!config.showAddButton || !product.available || product.templateSuffix === 'coming-soon' || (product.tags || []).indexOf('art') !== -1) {
      return html;
    }

    var plus = '<span class="te-card__plus" aria-hidden="true">+</span>';

    if (product.variantCount === 1 && product.templateSuffix !== 'pre-order') {
      return html
        + '<button type="button" class="te-quick-add" data-te-quick-add data-variant-id="' + escapeHtml(product.variantId) + '">'
        + plus + '<span class="te-quick-add__label">' + escapeHtml(labels.add) + '</span>'
        + '</button>';
    }

    if (product.templateSuffix === 'pre-order') {
      return html
        + '<a href="' + url + '" class="te-quick-add">'
        + plus + '<span class="te-quick-add__label">' + escapeHtml(labels.preOrder) + '</span>'
        + '</a>';
    }

    // Products with options open the quick view (the link still works if it can't)
    return html
      + '<a href="' + url + '" class="te-quick-add" data-te-quickview data-product-handle="' + escapeHtml(product.handle) + '">'
      + plus + '<span class="te-quick-add__label">' + escapeHtml(labels.options) + '</span>'
      + '</a>';
  }

  // Money texts are inserted as given: they come formatted from the shop's money format
  function priceHtml(product, config) {
    if (product.templateSuffix === 'coming-soon') {
      return '';
    }

    var labels = config.labels;
    var html = '<div class="te-card__price">';

    if (product.onSale) {
      html += '<span class="u-visually-hidden">' + escapeHtml(labels.regularPrice) + '</span>'
        + '<s class="te-card__compare" data-money-convertible>' + product.compareAtText + '</s>'
        + '<span class="u-visually-hidden">' + escapeHtml(labels.salePrice) + '</span>'
        + '<span class="te-card__sale" data-money-convertible>' + product.priceText + '</span>';
    } else if (product.priceVaries) {
      html += '<span class="te-card__amount">'
        + escapeHtml(labels.fromPrice).replace('[min_price]', '<span data-money-convertible>' + product.priceText + '</span>')
        + '</span>';
    } else {
      html += '<span class="te-card__amount" data-money-convertible>' + product.priceText + '</span>';
    }

    return html + '</div>';
  }

  function render(product) {
    var config = window.teProductCardConfig;
    var url = escapeHtml(product.url);
    var html = '<div class="ProductItem te-card">';

    // Without the settings (snippet missing) still show a working link
    if (!config) {
      return html + '<div class="te-card__info"><h3 class="te-card__title"><a href="' + url + '">' + escapeHtml(cardTitle(product.title)) + '</a></h3></div></div>';
    }

    html += '<div class="te-card__media te-card-media">'
      + imagesHtml(product, config, url)
      + (product.showLabels === false ? '' : badgesHtml(product, config))
      + (product.hideActions ? '' : actionsHtml(product, config, url))
      + '</div>'
      + '<div class="te-card__info">';

    if (config.showVendor && !isBlank(product.vendor)) {
      html += '<p class="te-card__vendor">' + escapeHtml(product.vendor) + '</p>';
    }

    html += '<h3 class="te-card__title"><a href="' + url + '">' + escapeHtml(cardTitle(product.title)) + '</a></h3>'
      + priceHtml(product, config)
      + '</div>';

    return html + '</div>';
  }

  window.teProductCard = {
    render: render,
    badgesHtml: function (product) {
      return window.teProductCardConfig ? badgesHtml(product, window.teProductCardConfig) : '';
    }
  };
})();
